import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { AD_TYPES, GOALS } from "components/core/blocks/AdPlan/constants";
import ToolTips from "utils/toolTips";
import NoImage from "assets/images/utility/no_image.png";
import moment from "moment";
import getURLPrefix from "utils/getUrlPrefix";
import { formatCurrency } from "utils/formatNumber";

import TitleLink from "components/core/basic/TitleLink";
import { getDashboardTo } from "../../../views/AdvertisingOverview";
import { useAppSelector } from "../../../redux/hooks";
import { css, cx } from "../../../../styled-system/css";
import { type GVAdvertisement } from "../types";
import { useMemo } from "react";

const URL_PREFIX = getURLPrefix();

const displayAdStatus = (
  status_id: number,
  state_id: number,
  moderation_status: string | null = null,
  ad_type: number | null = null,
  ad_serving_status: string | null = null,
):
  | {
      icon: string;
      text: string;
      color: string;
    }
  | undefined => {
  const success = "pe-7s-check";
  const progress = "pe-7s-more";
  const failed = "pe-7s-attention";
  const paused = "pe-7s-close-circle";
  const ended = "pe-7s-less";
  if (ad_type === AD_TYPES.SPONSORED_DISPLAY_V2.value && typeof moderation_status === "string") {
    return { icon: failed, text: "creative_failed", color: "#CF1E10" };
  } else if (ad_serving_status === "ENDED") {
    return { icon: ended, text: "Ended", color: "#af55b5" };
  } else if (status_id === 1) {
    return { icon: success, text: "Success", color: "#25B50E" };
  } else if (status_id === 10) {
    return { icon: failed, text: "Failed", color: "#CF1E10" };
  } else if (status_id === 2) {
    return { icon: progress, text: "Pending", color: "#eb8c07" };
  } else if (status_id === 3 || (status_id === 11 && state_id === 0)) {
    return { icon: paused, text: "Paused", color: "#2b00ff" };
  } else if (status_id === 11) {
    return { icon: success, text: "Approved", color: "#25B50E" };
  } else if (status_id === 22) {
    return { icon: progress, text: "In Progress", color: "#eb8c07" };
  } else if (status_id === 20) {
    return { icon: failed, text: "Rejected", color: "#CF1E10" };
  }
};

interface Props {
  ad: GVAdvertisement;
  seasonalCategory?: boolean;
  className?: string;
}

export const AdvertisementCell = ({ ad, seasonalCategory, className }: Props) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const adId = ad.ad_id;
  const image = `${URL_PREFIX}/api/productimage/?advertisement=${adId}`;
  const adStatusDisplay = displayAdStatus(ad.ad_status, ad.state, ad.moderation_status, ad.ad_type, ad.serving_status);

  let NTMBadge = "hidden";

  if (ad.ad_goal === GOALS.IMPORTED_ADS.value) NTMBadge = "non-managed";
  if (ad.non_trellis_enrolled_ad) NTMBadge = "enrolled";
  if (ad.non_trellis_enrolled_ad_accept_bids) NTMBadge = "accept_bids";

  let { scheduled_actions: scheduledActions, tags, program_name: biddingProgramName } = ad;

  const filteredScheduledActions = useMemo(() => {
    return (
      scheduledActions?.filter(
        (sa) =>
          sa.scheduled_action_status !== undefined && ["scheduled", "active"].includes(sa.scheduled_action_status),
      ) ?? []
    );
  }, [scheduledActions]);

  if (!biddingProgramName && ad.program_name) {
    biddingProgramName = ad.program_name;
  }

  return (
    <div className={cx(css({ display: "flex", fontSize: "1.5rem", fontWeight: "500" }), className)}>
      <div
        className={css({
          flexShrink: 0,
          width: 50,
          height: 50,
          marginRight: "10px",
          userSelect: "none",
        })}
        style={{ background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain` }}
        draggable="false"
      />
      <div className="overflow-ellipsis pt-2 title-link" style={{ textAlign: "left" }}>
        <TitleLink
          to={getDashboardTo({
            type: "advertisement",
            id: adId,
          })}
          style={{
            textDecoration: "none",
            fontWeight: "500",
            fontSize: "1.5rem",
            cursor: "pointer",
          }}
          title={ad.ad_name}
        />
        <div className="d-flex align-items-center">
          <small
            className="text-nowrap pe-3"
            style={{
              fontWeight: "light",
              color: "#666",
              fontSize: "1rem",
            }}
          >
            <i>
              {ad.ad_type === AD_TYPES.SPONSORED_PRODUCT_WALMART.value && localStorage.channel === "walmart" ?
                AD_TYPES.SPONSORED_PRODUCT_WALMART.label
              : (
                ad.ad_type === AD_TYPES.SPONSORED_BRAND_V2.value &&
                ["_SB_BV_", "_SB_V_"].includes(ad.creative?.brand_ad_type ?? "")
              ) ?
                AD_TYPES.SPONSORED_BRAND_VIDEO.label
              : ad.ad_type === AD_TYPES.SPONSORED_BRAND_V2.value && !ad.parent ?
                AD_TYPES.SPONSORED_BRAND_V2.label
              : Object.values(AD_TYPES).find((adTypeId) => ad.ad_type === adTypeId.value)?.label}
            </i>
            <span
              style={{
                display: "inline-block",
                fontWeight: "bold",
                marginLeft: "1rem",
                fontSize: "18px",
                verticalAlign: "middle",
              }}
            >
              {adStatusDisplay && (
                <ToolTips
                  id={`adStatus-${adId}`}
                  toolTip={adStatusDisplay.text}
                  icon={adStatusDisplay.icon}
                  position="top"
                  color={adStatusDisplay.color}
                />
              )}
            </span>
          </small>
          {NTMBadge === "non-managed" && (
            <Badge className="bg-trellis-purple me-2" pill>
              Non Managed
            </Badge>
          )}
          {NTMBadge === "enrolled" && (
            <Badge className="bg-trellis-fuchsia me-2" pill>
              NTM - Enrolled
            </Badge>
          )}
          {NTMBadge === "accept_bids" && (
            <Badge className="bg-trellis-green me-2" pill>
              NTM - Bids Accepted
            </Badge>
          )}
          {ad.campaign_status === "CAMPAIGN_OUT_OF_BUDGET" ?
            <Badge
              className={`badge bg-trellis-purple me-2`}
              pill
              style={{
                borderRadius: "5px",
              }}
            >
              <span className="fs-6 ">Out of Budget </span>
            </Badge>
          : null}

          {ad.start_date || ad.end_date ?
            <OverlayTrigger
              overlay={
                <Tooltip placement="top" className="in" id="tooltip-top" bsPrefix="tr-popover-alt bg-purple">
                  <span className="text-center mx-auto fs-5">
                    Manual budget change scheduled <br />
                    {moment(ad.start_date).format("MMMM D")} - {moment(ad.end_date).format("MMMM D")}
                  </span>
                </Tooltip>
              }
            >
              <Badge
                className={`badge bg-trellis-mauve me-2`}
                pill
                style={{
                  borderRadius: "5px",
                }}
              >
                <span className="fs-6 ">Scheduled Bids </span>
              </Badge>
            </OverlayTrigger>
          : null}

          {seasonalCategory ?
            <Badge className="bg-trellis-fuchsia" pill>
              Seasonality
            </Badge>
          : null}
        </div>
        {filteredScheduledActions.length ?
          filteredScheduledActions.map((scheduledAction, index) => (
            <Badge
              key={`scheduledActionAd${ad.ad_id}-${index}`}
              className={`badge bg-trellis-purple me-3`}
              pill
              style={{
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              <span className="fs-6 ">
                {scheduledAction.scheduled_action_fields[0]?.change_type === "percentage_change" ?
                  `${(scheduledAction.scheduled_action_fields[0].change_value ?? 0) > 1 ? "+" : ""}${Math.trunc(Math.round((scheduledAction.scheduled_action_fields[0].change_value ?? 0) * 100) - 100)}%`
                : formatCurrency(
                    scheduledAction.scheduled_action_fields[0]?.change_value ?? 0,
                    marketPlace,
                    false,
                    true,
                  )
                }{" "}
                Budget Update: {moment(scheduledAction.scheduled_action_start_date).utc().format("MMM D")}-
                {moment(scheduledAction.scheduled_action_end_date).utc().format("MMM D")}{" "}
              </span>
            </Badge>
          ))
        : null}
        {biddingProgramName ?
          <Badge className="bg-trellis-fuchsia me-3" pill>
            Bidding Rules: {biddingProgramName}
          </Badge>
        : null}
        {(ad.day_parting_method ?? 0) > 0 ?
          <Badge className="bg-trellis-fuchsia me-3" pill>
            Dayparting
          </Badge>
        : null}
        {tags?.map((tag, index) => (
          <Badge
            className="bg-trellis-mauve me-3"
            style={{
              cursor: "pointer",
            }}
            pill
            key={`${ad.ad_id}-customTags-${index}`}
          >
            {tag}
          </Badge>
        ))}
      </div>
    </div>
  );
};
